<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Videoaulas</h5>
      <router-link
        v-if="hasPermission('adicionar_videoaulas')"
        to="/ajuda/videoaulas/novo"
      >
        <b-button variant="primary" class="d-flex align-items-center">
          <i class="bi bi-file-earmark-plus-fill mr-2" title="Nova Videoaula"></i>
        </b-button>
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.titulo"
        placeholder="Filtrar por título"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.descricao"
        placeholder="Filtrar por descrição"
        class="form-control"
        @input="filtrar"
      />
      <b-form-select
        v-model="filters.publico"
        :options="publicoOptions"
        class="form-control"
        :disabled="isInstrutor"
        @change="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('titulo')">
              Título {{ arrow('titulo') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('descricao')">
              Descrição {{ arrow('descricao') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('publico')">
              Público {{ arrow('publico') }}
            </button>
          </th>
          <th class="text-left">Link</th>
          <th class="text-center">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="v in videoaulas" :key="v.id">
          <td>{{ v.titulo }}</td>
          <td>{{ v.descricao }}</td>
          <td>{{ formatPublico(v.publico) }}</td>
          <td class="text-left">
            <template v-if="v.video_url">
              <a
                href="#"
                @click.prevent="markAndOpen(v)"
                class="file-link"
                title="Abrir player"
              >
                {{ v.video_url }}
              </a>
              <!-- badge “new” se não visualizado -->
              <i
                v-if="v.visualizado === false"
                class="bi bi-patch-exclamation-fill text-info ms-1"
                title="Novo"
              ></i>
            </template>
            <span v-else>–</span>
          </td>
          <td class="text-end">
            <!-- <router-link
              v-if="hasPermission('detalhar_videoaulas')"
              :to="`/ajuda/videoaulas/${v.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary me-2" title="Detalhar"></i>
            </router-link> -->
            <router-link
              v-if="hasPermission('alterar_videoaulas')"
              :to="`/ajuda/videoaulas/${v.id}`"
            >
              <i class="bi bi-pencil-fill text-warning me-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_videoaulas')"
              class="bi bi-trash-fill text-danger me-2"
              title="Excluir"
              @click="excluir(v.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'

const { fetchPermissions, hasPermission } = usePermissions()
const { appContext } = getCurrentInstance()
const perfil = appContext.config.globalProperties.$globalState.userProfile

const {
  items: videoaulas,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort: changeSort,
  arrow
} = useSortableTable(
  `/ajuda/videoaulas`,
  { key: 'criado_em', order: 'desc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])
const startRow    = computed(() => (page.value - 1) * limit.value + 1)
const endRow      = computed(() => Math.min(page.value * limit.value, totalItems.value))

const isInstrutor = computed(() => !!perfil?.includes('Instrutor'))

const publicoOptions = [
  { value: '', text: 'Todos os públicos' },
  { value: 'Instrutor', text: 'Instrutor' },
  { value: 'Apoio', text: 'Apoio' },
  { value: 'Diretoria', text: 'Diretoria' }
]

function formatPublico(p) {
  if (Array.isArray(p)) return p.join(', ')
  if (typeof p === 'string') return p
  return '–'
}

function filtrar() {
  page.value = 1
  load()
}

function limparFiltros() {
  filters.value.titulo = ''
  filters.value.descricao = ''
  if (!isInstrutor.value) {
    filters.value.publico = ''
  } else {
    filters.value.publico = 'Instrutor'
  }
  page.value = 1
  load()
}

function changeLimit() {
  page.value = 1
  load()
}
async function excluir(id) {
  if (confirm('Excluir essa videoaula?')) {
    await axios.delete(`/ajuda/videoaulas/${id}`)
    load()
  }
}

function getEmbedUrl(watchUrl) {
  try {
    const url = new URL(watchUrl)
    // Pode ser “v=ID” ou share link “youtu.be/ID”
    let id = url.searchParams.get('v')
    if (!id && url.hostname === 'youtu.be') {
      id = url.pathname.slice(1)
    }
    return id
      ? `https://www.youtube.com/embed/${id}`
      : watchUrl  // fallback: retorna a original caso falhe
  } catch {
    return watchUrl
  }
}

function markAndOpen(v) {
  // 1) marca como visualizado se ainda não foi
  if (v.visualizado === false) {
    axios
      .post('/ajuda/visualizacoes', {
        tipo:    'videoaula',
        item_id: v.id
      })
      .catch(err => console.error(err))
      .finally(() => {
        // atualiza só este item
        v.visualizado = true
      })
  }
  // 2) abre player num popup
  const url = getEmbedUrl(v.video_url)
  window.open(url, '_blank', 'noopener')
}



watch(page, load)
watch(limit, () => {
  page.value = 1
  load()
})

onMounted(async () => {
  await fetchPermissions()
  // Instrutor só vê seu público
  if (perfil?.includes('Instrutor')) {
    filters.value.publico = 'Instrutor'
  }
  filters.value.titulo = ''
  filters.value.descricao = ''
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
.table-hover tr:hover { background-color: var(--bs-light); }

.file-link {
  text-decoration: none;
}
/* opcional: underline só ao passar o mouse */
.file-link:hover {
  text-decoration: underline;
}
</style>
